import { formatDistanceToNow } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import {
    Bell,
    CheckCircle2,
    MessageSquare,
    UserPlus,
    AlertCircle,
    Clock,
    AtSign,
    Trash2,
    Check,
    ExternalLink,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/lib/i18n';
import type { Notification, NotificationType } from '@/lib/schemas';
import { Button } from '@/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

// ============================================
// NOTIFICATION ICON
// ============================================

interface NotificationIconProps {
    type: NotificationType;
    className?: string;
}

export function NotificationIcon({ type, className }: NotificationIconProps) {
    const iconClass = cn('h-4 w-4', className);

    switch (type) {
        case 'TASK_ASSIGNED':
            return <UserPlus className={cn(iconClass, 'text-primary')} />;
        case 'TASK_UPDATED':
            return <CheckCircle2 className={cn(iconClass, 'text-blue-500')} />;
        case 'COMMENT_ADDED':
            return <MessageSquare className={cn(iconClass, 'text-green-500')} />;
        case 'MENTION':
            return <AtSign className={cn(iconClass, 'text-yellow-500')} />;
        case 'PROJECT_INVITE':
            return <UserPlus className={cn(iconClass, 'text-purple-500')} />;
        case 'DUE_DATE_REMINDER':
            return <Clock className={cn(iconClass, 'text-orange-500')} />;
        default:
            return <Bell className={cn(iconClass, 'text-foreground-muted')} />;
    }
}

// ============================================
// NOTIFICATION ITEM
// ============================================

interface NotificationItemProps {
    notification: Notification;
    onMarkRead?: (id: string) => void;
    onDelete?: (id: string) => void;
    onClick?: (notification: Notification) => void;
}

export function NotificationItem({
    notification,
    onMarkRead,
    onDelete,
    onClick,
}: NotificationItemProps) {
    const navigate = useNavigate();
    const { t, language } = useI18n();
    const locale = language === 'fr' ? fr : enUS;
    const timeAgo = formatDistanceToNow(new Date(notification.createdAt), {
        addSuffix: true,
        locale,
    });

    const handleViewInvite = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (notification.data?.inviteToken) {
            navigate(`/invite/${notification.data.inviteToken}`);
        }
    };

    const handleClick = () => {
        // For project invites, only navigate via the "View Invitation" button
        // Don't navigate to the project directly since user hasn't joined yet
        if (notification.type === 'PROJECT_INVITE') {
            // Just mark as read, don't navigate
            if (onClick) {
                // Call onClick to mark as read but don't navigate
                onClick(notification);
            }
            return;
        }
        // For other notification types, use the default click handler
        onClick?.(notification);
    };

    const isProjectInvite = notification.type === 'PROJECT_INVITE' && notification.data?.inviteToken;

    return (
        <div
            className={cn(
                'group flex items-start gap-3 p-3 rounded-lg transition-colors cursor-pointer',
                notification.isRead
                    ? 'bg-transparent hover:bg-surface-hover'
                    : 'bg-primary/5 hover:bg-primary/10'
            )}
            onClick={handleClick}
        >
            {/* Unread indicator */}
            <div className="flex-shrink-0 pt-1">
                {!notification.isRead && (
                    <div className="h-2 w-2 rounded-full bg-primary" />
                )}
                {notification.isRead && <div className="h-2 w-2" />}
            </div>

            {/* Icon */}
            <div className="flex-shrink-0 pt-0.5">
                <NotificationIcon type={notification.type} />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                    {notification.title}
                </p>
                <p className="text-sm text-foreground-secondary mt-0.5 line-clamp-2">
                    {notification.message}
                </p>
                <p className="text-xs text-foreground-muted mt-1">{timeAgo}</p>

                {/* Action button for project invites */}
                {isProjectInvite && (
                    <Button
                        size="sm"
                        className="mt-2"
                        onClick={handleViewInvite}
                    >
                        <ExternalLink className="h-3 w-3 mr-1" />
                        {t('notifications.viewInvitation')}
                    </Button>
                )}
            </div>

            {/* Actions */}
            <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <AlertCircle className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                        {!notification.isRead && onMarkRead && (
                            <DropdownMenuItem
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onMarkRead(notification.id);
                                }}
                            >
                                <Check className="h-4 w-4 mr-2" />
                                Mark as read
                            </DropdownMenuItem>
                        )}
                        {onDelete && (
                            <DropdownMenuItem
                                className="text-destructive"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(notification.id);
                                }}
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                            </DropdownMenuItem>
                        )}
                    </DropdownMenuContent>
                </DropdownMenu>
            </div>
        </div>
    );
}

// ============================================
// NOTIFICATION LIST
// ============================================

interface NotificationListProps {
    notifications: Notification[];
    onMarkRead?: (id: string) => void;
    onDelete?: (id: string) => void;
    onNotificationClick?: (notification: Notification) => void;
    emptyMessage?: string;
}

export function NotificationList({
    notifications,
    onMarkRead,
    onDelete,
    onNotificationClick,
    emptyMessage = 'No notifications',
}: NotificationListProps) {
    if (notifications.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-8 text-center">
                <Bell className="h-12 w-12 text-foreground-muted mb-3" />
                <p className="text-sm text-foreground-muted">{emptyMessage}</p>
            </div>
        );
    }

    return (
        <div className="divide-y divide-border">
            {notifications.map((notification) => (
                <NotificationItem
                    key={notification.id}
                    notification={notification}
                    onMarkRead={onMarkRead}
                    onDelete={onDelete}
                    onClick={onNotificationClick}
                />
            ))}
        </div>
    );
}

// ============================================
// NOTIFICATION BADGE
// ============================================

interface NotificationBadgeProps {
    count: number;
    className?: string;
}

export function NotificationBadge({ count, className }: NotificationBadgeProps) {
    if (count <= 0) return null;

    return (
        <span
            className={cn(
                'absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground',
                className
            )}
        >
            {count > 99 ? '99+' : count}
        </span>
    );
}
